import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import LogoIcon from '../common/LogoIcon';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredPath, setHoveredPath] = useState(null);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'The Stay', path: '/stay' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Location', path: '/location' },
    { name: 'Rates', path: '/rates' },
    { name: 'Contact', path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setIsOpen(false);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-brand-cream/95 backdrop-blur-md shadow-lg py-2' 
        : 'bg-brand-cream py-5'
    }`}>
      <div className="container mx-auto px-6 max-w-7xl flex justify-between items-center">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-3 z-50 group" 
          onClick={closeMenu}
        >
          <div className="transition-transform duration-500 group-hover:rotate-[10deg]">
            <LogoIcon className="w-12 h-12 md:w-14 md:h-14" />
          </div>
          <div className="flex flex-col">
            <span className="font-serif text-xl md:text-2xl font-bold tracking-wider text-brand-olive uppercase leading-none transition-colors duration-300 group-hover:text-brand-dark">
              R. Venue
            </span>
            <span className="font-script text-xl md:text-2xl text-brand-sage -mt-1 transition-colors duration-300 group-hover:text-brand-olive">
              Apartelle
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              onMouseEnter={() => setHoveredPath(link.path)}
              onMouseLeave={() => setHoveredPath(null)}
              className={`relative px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                location.pathname === link.path ? 'text-brand-olive font-bold' : 'text-brand-sage hover:text-brand-olive'
              }`}
            >
              <span className="relative z-10">{link.name}</span>
              
              {/* Animated Underline */}
              {hoveredPath === link.path && (
                <motion.div
                  layoutId="nav-underline"
                  className="absolute bottom-0 left-4 right-4 h-0.5 bg-brand-olive/40"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              
              {/* Active State Indicator */}
              {location.pathname === link.path && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-brand-olive" />
              )}
            </Link>
          ))}
          
          <Link 
            to="/rates" 
            className="ml-4 bg-brand-olive text-brand-cream px-7 py-3 rounded-sm text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-brand-dark hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
          >
            Book Now
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="md:hidden text-brand-olive z-50 p-2 hover:bg-brand-olive/5 rounded-full transition-colors" 
          onClick={() => setIsOpen(!isOpen)}
        >
          <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
        </button>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-0 bg-brand-cream z-40 flex flex-col items-center justify-center md:hidden"
            >
              <div className="flex flex-col items-center gap-6 w-full px-12 text-center">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="w-full"
                  >
                    <Link 
                      to={link.path} 
                      onClick={closeMenu} 
                      className={`block py-2 text-2xl uppercase tracking-[0.2em] font-serif transition-all duration-300 active:scale-95 ${
                        location.pathname === link.path ? 'text-brand-olive font-bold' : 'text-brand-sage'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: navLinks.length * 0.1 }}
                  className="w-full mt-4"
                >
                  <Link 
                    to="/rates" 
                    onClick={closeMenu} 
                    className="block bg-brand-olive text-brand-cream py-4 rounded-sm text-lg uppercase tracking-[0.2em] font-bold w-full shadow-lg active:scale-95 transition-transform"
                  >
                    Book Now
                  </Link>
                </motion.div>
              </div>
              
              {/* Mobile Decorative Logo */}
              <div className="absolute bottom-12 opacity-10">
                <LogoIcon className="w-24 h-24" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;